import { GetContractReturnType, Hash } from 'viem';
import { PublicClient, WalletClient } from 'wagmi';

import {
  ActivePool__factory,
  PriceFeedV2__factory,
  BalancerVault__factory,
  BorrowerOperations__factory,
  CollSurplusPool__factory,
  CommunityIssuance__factory,
  GLPManager__factory,
  GMXStakingBonus__factory,
  HintHelpers__factory,
  SavingModuleStabilityPool__factory,
  SortedTroves__factory,
  StabilityPoolManager__factory,
  TroveManager__factory,
  VSTOracle__factory,
  VestaDexTrader__factory,
  VestaInterestManager__factory,
  VestaNFT__factory,
  VestaParameters__factory,
  VestaSavingModule__factory,
  VestingVesta__factory,
  AggregatorV3Interface__factory,
  BalLiquidityGaugeV5__factory,
  CurveFactoryPool__factory,
  ERC20__factory,
  FeeTracker__factory,
  FraxStaking__factory,
  StabilityPool__factory,
  VestaEIR__factory,
  VestaFarming__factory,
  VestaVaultStaking__factory,
} from '~config/contractTypes';
import { EContractName } from '~config/protocol/contractConfig';
import {
  AllVestaAssets,
  VestaFarm,
  ELPType,
  FraxFarm,
  BalFarm,
  EVestaAsset,
  ChainlinkAssets,
} from '~config/types';

export type AddressString = `0x${string}`;
export type AddressStringOrEmpty = AddressString | '';

export function findIsAddressString(value: unknown): value is AddressString {
  return typeof value === 'string' && value.startsWith('0x');
}

export type TAbi = readonly object[];

export type TContractFactory = { abi: TAbi };

export type TFactoryAbi<TFactory extends TContractFactory> = GetContractReturnType<
  TFactory['abi'],
  PublicClient,
  WalletClient
>;

export type IndexedContract<TAssetIndex extends string, TFactory extends TContractFactory> = {
  [index in TAssetIndex]?: TFactoryAbi<TFactory>;
};

export type TActivePool = TFactoryAbi<typeof ActivePool__factory>;
export type TPriceFeed = TFactoryAbi<typeof PriceFeedV2__factory>;
export type TCommunityIssuance = TFactoryAbi<typeof CommunityIssuance__factory>;
export type TStabilityPoolManager = TFactoryAbi<typeof StabilityPoolManager__factory>;
export type TVaultManager = TFactoryAbi<typeof TroveManager__factory>;
export type TVestaParameters = TFactoryAbi<typeof VestaParameters__factory>;
export type TCollateralSurplusPool = TFactoryAbi<typeof CollSurplusPool__factory>;
export type TBorrowerOperations = TFactoryAbi<typeof BorrowerOperations__factory>;
export type TVestingVesta = TFactoryAbi<typeof VestingVesta__factory>;
export type THintHelpers = TFactoryAbi<typeof HintHelpers__factory>;
export type TSortedTroves = TFactoryAbi<typeof SortedTroves__factory>;
export type TBalancerVault = TFactoryAbi<typeof BalancerVault__factory>;
export type TVestaNFT = TFactoryAbi<typeof VestaNFT__factory>;
export type TVSTOracle = TFactoryAbi<typeof VSTOracle__factory>;
export type TGLPManager = TFactoryAbi<typeof GLPManager__factory>;
export type TVestaDexTrader = TFactoryAbi<typeof VestaDexTrader__factory>;
export type TVestaInterestManager = TFactoryAbi<typeof VestaInterestManager__factory>;
export type TSavingModuleStabilityPool = TFactoryAbi<typeof SavingModuleStabilityPool__factory>;
export type TVestaSavingModule = TFactoryAbi<typeof VestaSavingModule__factory>;
export type TVestaGMXBonusStaking = TFactoryAbi<typeof GMXStakingBonus__factory>;

export interface NormalContracts {
  [EContractName.ActivePool]: TActivePool;
  [EContractName.PriceFeed]: TFactoryAbi<TPriceFeed>;
  [EContractName.CommunityIssuance]: TCommunityIssuance;
  [EContractName.StabilityPoolManager]: TStabilityPoolManager;
  [EContractName.VaultManager]: TVaultManager;
  [EContractName.VestaParameters]: TVestaParameters;
  [EContractName.CollateralSurplusPool]: TCollateralSurplusPool;
  [EContractName.BorrowerOperations]: TBorrowerOperations;
  [EContractName.VestingVesta]: TVestingVesta;
  [EContractName.HintHelpers]: THintHelpers;
  [EContractName.SortedTroves]: TSortedTroves;
  [EContractName.BalancerVault]: TBalancerVault;
  [EContractName.VestaNFT]: TVestaNFT;
  [EContractName.VSTOracle]: TVSTOracle;
  [EContractName.GLPManager]: TGLPManager;
  [EContractName.VestaDexTrader]: TVestaDexTrader;
  [EContractName.VestaInterestManager]: TVestaInterestManager;
  [EContractName.SavingModuleStabilityPool]: TSavingModuleStabilityPool;
  [EContractName.VestaSavingModule]: TVestaSavingModule;
  [EContractName.VestaGMXBonusStaking]: TVestaGMXBonusStaking;
}

export type TCurveFactoryPool = TFactoryAbi<typeof CurveFactoryPool__factory>;
export type TFraxStaking = TFactoryAbi<typeof FraxStaking__factory>;
export type TBalLiquidityGaugeV5 = TFactoryAbi<typeof BalLiquidityGaugeV5__factory>;
export type TVestaFarming = TFactoryAbi<typeof VestaFarming__factory>;
export type TERC20 = TFactoryAbi<typeof ERC20__factory>;
export type TStabilityPool = TFactoryAbi<typeof StabilityPool__factory>;
export type TFeeTracker = TFactoryAbi<typeof FeeTracker__factory>;
export type TVestaVaultStaking = TFactoryAbi<typeof VestaVaultStaking__factory>;
export type TAggregatorV3Interface = TFactoryAbi<typeof AggregatorV3Interface__factory>;
export type TVestaEIR = TFactoryAbi<typeof VestaEIR__factory>;

export interface IndexedContracts {
  CurveFactoryPool: IndexedContract<ELPType, TCurveFactoryPool>;
  FraxFarm: IndexedContract<FraxFarm, TFraxStaking>;
  BalGauge: IndexedContract<BalFarm, TBalLiquidityGaugeV5>;
  VestaFarm: IndexedContract<VestaFarm, TVestaFarming>;
  Asset: IndexedContract<AllVestaAssets, TERC20>;
  StabilityPool: IndexedContract<string, TStabilityPool>;
  FeeTracker: IndexedContract<EVestaAsset.GMX | EVestaAsset.sGLP, TFeeTracker>;
  VaultRewards: IndexedContract<EVestaAsset.GMX | EVestaAsset.sGLP, TVestaVaultStaking>;
  ChainlinkOracles: IndexedContract<ChainlinkAssets, TAggregatorV3Interface>;
  VestaEIR: IndexedContract<EVestaAsset, TVestaEIR>;
}

export type ContractContextTypes = keyof NormalContracts | keyof IndexedContracts;

export interface ContractsContext {
  normal: NormalContracts;
  indexed: IndexedContracts;
}

export enum TxStatus {
  idle = 'idle',
  error = 'error',
  success = 'success',
  confirming = 'confirming',
  sending = 'sending',
}

export interface OnTransactionSuccess {
  callback: () => Promise<any>;
  checkBlock?: boolean;
}

export interface TxInstance {
  key: string;
  status: TxStatus;
  isTransacting: boolean;
  complete: boolean;
  url: string;
  error: string;
  nextStepName: string;
}

export interface TxContext {
  transactions: { [index: string]: TxInstance };
  history: TxHistorical[];
  transact: (params: {
    key: string;
    writeContract: () => Promise<Hash>;
    onTransactionSuccess: OnTransactionSuccess;
  }) => Promise<any> | undefined;
  createTxInstance: (args: { key: string; nextStepName?: string; override?: boolean }) => void;
  getTx: (key: string) => TxInstance | undefined;
  clearHistory: () => void;
  currentTransactionKey: string;
  prevTransactionKey: string;
  showTransaction: boolean;
}

export type TxHistorical = TxInstance & {
  timestampUnix: number;
  timestampExplicit: string;
};
